"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RegionAdminPage = exports.getArrayValue = exports.getValue = exports.delimiter = void 0;
const StorageClient_1 = require("@juvare/platform-sdk-js/StorageClient");
const shared_1 = require("../../../../backend/features/default-classifiers/shared");
const usePlatformSdkReactTranslation_1 = require("../../../../ui/hooks/usePlatformSdkReactTranslation");
const react_1 = __importDefault(require("react"));
const classifiers_1 = require("../../classifiers");
exports.delimiter = ';';
const getValue = (value) => (value ? value.trim() : '');
exports.getValue = getValue;
const getArrayValue = (value) => value && value.trim()
    ? value
        .split(exports.delimiter)
        .map(exports.getValue)
        .filter((value) => !!value)
    : [];
exports.getArrayValue = getArrayValue;
function getEntityTitle(row) {
    return row.columns.name;
}
const EntityDetailsComponent = (props) => {
    const { t } = (0, usePlatformSdkReactTranslation_1.usePlatformSdkReactTranslation)();
    return (react_1.default.createElement(classifiers_1.SlideoutFields, { fields: [
            { label: t('defaultClassifiers.state'), value: props.response.data.state },
            { label: t('defaultClassifiers.counties'), value: props.response.data.counties.join(', ') },
            { label: t('common.status'), value: t(`classifiers.statusEnum.${props.response.data.status}`) },
            { label: t('classifiers.displayOrder'), value: props.response.data.displayOrder ?? react_1.default.createElement("em", null, "(not set)") },
        ] }));
};
const csvFileHeader = ['ID', 'Name', 'State Code', 'Counties', 'Status', 'Display Order'];
async function mapCsvRowToData(row) {
    const id = (row.data[0] ?? '').trim();
    const name = (row.data[1] ?? '').trim();
    const state = (row.data[2] ?? '').trim();
    const counties = (row.data[3] ?? '').trim();
    const status = (row.data[4] ?? '').trim().toLowerCase();
    const displayOrder = (row.data[5] ?? '').trim();
    const validCounties = [];
    if (name.length === 0) {
        row.errors.push('Name is required');
    }
    if (status.length === 0) {
        row.errors.push('Status is required');
    }
    if (state.length === 0) {
        row.errors.push('State Code is required');
    }
    if (counties.length === 0) {
        row.errors.push('Counties are required');
    }
    if (counties) {
        const countiesValues = (0, exports.getArrayValue)(counties);
        countiesValues.forEach((county) => {
            getJurisdictionByName(state, row.errors, 'County not found: ' + county, [
                { column: 'name', op: '=', value: county },
                { column: 'type', op: '=', value: 'county' },
                { column: 'status', op: '=', value: 'active' },
            ]).then((columns) => columns && validCounties.push(columns.name));
        });
        await checkIfCountiesAreUsed(name, validCounties, row.errors);
    }
    switch (status) {
        case 'active':
        case 'deprecated':
        case 'retired':
            break;
        default:
            row.errors.push('Invalid status, expected one of: active, deprecated, retired');
    }
    let displayOrderNumber;
    if (displayOrder) {
        displayOrderNumber = parseFloat(displayOrder);
        if (!isFinite(displayOrderNumber)) {
            row.errors.push('Invalid display number, expected a number');
            displayOrderNumber = undefined;
        }
    }
    if (row.errors.length === 0) {
        row.object = {
            id: id,
            data: {
                name: name,
                state: state ?? '',
                counties: validCounties ?? '',
                status: status,
                displayOrder: displayOrderNumber,
            },
        };
    }
    return Promise.resolve(row);
}
async function mapDataToCsvRow(response) {
    return [
        response.id,
        response.data.name,
        response.data.state,
        response.data.counties.join(';').toString(),
        response.data.status,
        response.data.displayOrder != null ? response.data.displayOrder.toString() : '',
    ];
}
async function getJurisdictionByName(value, errors, errorMessage, filter) {
    if (value) {
        const result = await new StorageClient_1.StorageClient('jurisdiction').list({
            filter: [{ column: 'parentPostalCode', op: '=', value }, ...(filter ? filter : [])],
            limit: 1,
        });
        if (result.rows.length > 0) {
            return result.rows[0].columns;
        }
        else {
            errors.push(errorMessage);
        }
    }
    return undefined;
}
async function checkIfCountiesAreUsed(regionName, values, errors) {
    if (values.length > 0) {
        const result = await new StorageClient_1.StorageClient('region').list({
            filter: [
                { column: 'counties', op: 'overlap', value: values },
                { column: 'name', op: '=', value: regionName },
            ],
        });
        if (result.rows.length > 0) {
            const duplicates = [];
            result.rows.forEach((row) => {
                row.columns.counties.forEach((resultCounty) => {
                    if (values.includes(resultCounty)) {
                        duplicates.push(resultCounty);
                    }
                });
            });
            errors.push('These counties/parishes: ' + duplicates.join(', ') + 'are used in: ' + regionName);
        }
    }
}
const RegionAdminPage = ({ includeTerritories }) => {
    const { t } = (0, usePlatformSdkReactTranslation_1.usePlatformSdkReactTranslation)();
    const formConfig = {
        type: 'form',
        children: [
            {
                type: 'form.text',
                key: 'name',
                label: t('common.name'),
                validate: {
                    required: true,
                },
            },
            {
                type: 'form.select',
                key: 'state',
                label: includeTerritories ? t('defaultClassifiers.stateAndTerritory') : t('defaultClassifiers.state'),
                validate: {
                    required: true,
                },
                valueProvider: { type: includeTerritories ? 'classifier.stateAndTerritory' : 'classifier.state' },
            },
            {
                type: 'form.multiSelect',
                key: 'counties',
                label: t('defaultClassifiers.counties'),
                validate: {
                    required: true,
                },
                valueProvider: { type: 'classifier.county', paramsFrom: '^state' },
            },
            {
                type: 'form.select',
                key: 'status',
                label: t('common.status'),
                validate: {
                    required: true,
                },
                values: [
                    {
                        value: 'active',
                        label: t('classifiers.statusEnum.active'),
                    },
                    {
                        value: 'deprecated',
                        label: t('classifiers.statusEnum.deprecated'),
                    },
                    {
                        value: 'retired',
                        label: t('classifiers.statusEnum.retired'),
                    },
                ],
            },
            {
                type: 'form.number',
                key: 'displayOrder',
                label: t('classifiers.displayOrder'),
            },
        ],
    };
    return (react_1.default.createElement(classifiers_1.ClassifierAdminPage, { entity: shared_1.regionEntityName, title: t('defaultClassifiers.region'), fullTextSearchField: 'search', getEntityTitle: getEntityTitle, entityDetailsComponent: EntityDetailsComponent, formConfig: formConfig, csvFileHeader: csvFileHeader, mapCsvRowToData: mapCsvRowToData, mapDataToCsvRow: mapDataToCsvRow }));
};
exports.RegionAdminPage = RegionAdminPage;
